<template>
  <BonusCard
    :id="freespin.userFreespinProgramId"
    type="freespin"
    :timer="bonusTimer"
    :level="freespin.bonusForDepositProgram?.level"
    :paused="rollingStatus === 'paused'"
    :progress="progressBar"
    :steps-progress="stepsProgress"
    data-t="freespin-card"
  >
    <template #subtitle>
      <div class="amount-container">
        <div class="subtitle-block">
          <StIcon
            v-if="isEnabledStepsRolling"
            class="lock-icon"
            name="lock-solid"
            :size="16"
          />
          <span>{{ subtitleByStatus }}</span>
        </div>
        <div v-if="isEnabledStepsRolling" class="steps-amount">
          <div
            v-if="unlockedAmount"
            class="steps-amount-info"
            data-t="unlocked-amount"
          >
            {{ t('bonuses.unlockedAmount') }}
            {{ formatFiat(unlockedAmount) }}
          </div>
          <div v-else class="steps-amount-info" data-t="until-next-claim">
            {{ t('bonuses.untilNextClaim') }}
            {{ formatFiat(untilNextClaimAmount) }}
          </div>
        </div>
      </div>
    </template>
    <BonusCardBorderedContent
      v-if="isProccessingLastFreespin"
      class="border-wrapper"
      data-t="freespin-handling"
    >
      <StSpinner :size="20" />
      <span class="handling-title">
        {{ t('bonuses.resultHandlingTitle') }}
      </span>
    </BonusCardBorderedContent>
    <div
      v-else-if="status === 'new' && +freespin.rollingMultiplier > 0"
      class="card-body"
      data-t="terms"
    >
      <BonusCardBorderedContent>
        <BonusCardTerms :list="termsList" />
      </BonusCardBorderedContent>
    </div>

    <template v-if="status === 'inProgress'" #progress-text>
      <div class="progress-data">
        <span class="game-name">
          {{ gameName }}
        </span>
        <span class="current-progress" data-t="current-progress">
          <span class="current-progress-text">{{ t('bonuses.left') }}</span>
          {{ progressSubtitle }}
        </span>
      </div>
    </template>
    <template v-else-if="status === 'rolling'" #progress-text>
      <div class="rolling-data">
        <span class="wager">
          {{ t('bonuses.wager', { multiplier: wager }) }}
        </span>
        <span class="current-rolling" data-t="current-rolling">
          {{ rollingSubtitle }}
        </span>
      </div>
    </template>

    <template #actions>
      <div class="actions-wrapper">
        <StButton
          v-if="isVisiblePartClime"
          class="button"
          type="primary"
          :is-pulsing="!isRollingStatusNewOrPaused"
          :loading="partClaimStatus === 'pending'"
          :disabled="['success', 'error'].includes(partClaimStatus)"
          :label="t('bonuses.takeBonus')"
          data-t="part-rolling-button"
          @click="handlePartClaim"
        />
        <StButton
          v-else-if="!isProccessingLastFreespin"
          block
          class="button"
          :type="buttonType"
          :icon="buttonIcon"
          :disabled="isProccessingLastFreespin"
          is-left-icon
          :label="buttonLabel"
          @click="onSubmitClick"
        />
      </div>
    </template>
    <LottieClaimAnimation ref="claimAnimation" :is-big="isMobile" />
  </BonusCard>
</template>

<script setup lang="ts">
import { delay } from '@st/utils'
import LottieClaimAnimation from '@st/ui/components/LottieClaimAnimation/LottieClaimAnimation.client.vue'
import BonusCard from '../../BonusCard/BonusCard.vue'
import BonusCardTerms from '../../BonusCard/parts/BonusCardTerms.vue'
import BonusCardBorderedContent from '../../BonusCard/parts/BonusCardBorderedContent.vue'
import type { Freespin } from '../../../types'
import { useFreespin } from '../../../composables/useFreespin'
import { useBonusStepRolling } from '../../../composables/useBonusStepRolling'

const animation =
  useTemplateRef<InstanceType<typeof LottieClaimAnimation>>('claimAnimation')

const props = defineProps<{ freespin: Freespin; mobile?: boolean }>()
const { t } = useI18n()
const { freespin } = toRefs(props)
const { isMobile } = usePlatform()

const {
  buttonLabel,
  isProccessingLastFreespin,
  status,
  buttonType,
  buttonIcon,
  rollingStatus,
  gameName,
  freespinBetAmount,
  currentRewardAmount,
  wager,
  bettingProgress,
  rollingProgress,
  currentRollingAmount,
  fullRollingAmount,
  isRollingStatusNewOrPaused,
  formatFiat,
  onSubmitClick,
  fetchFreespins,
} = useFreespin(freespin)

const subtitleByStatus = computed(() => {
  if (status.value === 'new') {
    return `${freespin.value.freespinNumber} FS × ${freespinBetAmount.value}`
  }

  return currentRewardAmount.value
})

const bonusTimer = computed(() => {
  if (isProccessingLastFreespin.value) return null

  return {
    expiresAt: freespin.value.expiredAt,
    title: t('bonuses.timerTitle'),
  }
})

const termsList = computed(() => [
  {
    label: t('bonuses.spinCondition'),
    value: t('bonuses.wager', {
      multiplier: freespin.value.rollingMultiplier,
    }),
  },
])

const progressSubtitle = computed(
  () =>
    `${freespin.value.freespinNumberLeft} / ${freespin.value.freespinNumber} FS`,
)

const rollingSubtitle = computed(
  () => `${currentRollingAmount.value} / ${fullRollingAmount.value}`,
)

const progressBar = computed(() => {
  if (rollingStatus.value === 'rolled') return ''

  if (status.value === 'inProgress') {
    return bettingProgress.value
  }

  if (status.value === 'rolling') {
    return rollingProgress.value
  }

  return ''
})

const stepRollingData = computed(() => ({
  stepRollingData: freespin.value.rollingSteps ?? [],
  rollingData: freespin.value.rolling,
  bonusAmount: freespin.value.freespinCurrentRewardAmount ?? '0',
}))
const {
  unlockedAmount,
  untilNextClaimAmount,
  canPartClaim,
  partClaim,
  partClaimStatus,
  isEnabledStepsRolling,
  stepsProgress,
} = useBonusStepRolling(stepRollingData)

async function handlePartClaim() {
  await partClaim()
  if (partClaimStatus.value === 'success') {
    await animation.value?.runClaimAnimation()
    await delay(300)
    await fetchFreespins()
  }
}

const isVisiblePartClime = computed(
  () => canPartClaim.value || rollingStatus.value === 'rolled',
)
</script>
<style scoped>
.actions-wrapper {
  position: relative;
  display: flex;
  flex: 1 1 50%;
  min-width: 0;
}

.button {
  flex: 1 1 0;
  min-width: 0;
}

.progress-data,
.rolling-data {
  display: flex;
  justify-content: space-between;
}

.current-progress {
  color: var(--text-primary);

  .current-progress-text {
    color: var(--text-secondary);
  }
}

.card-body {
  min-height: var(--spacing-400);
}

.amount-container {
  display: flex;
  gap: var(--spacing-050);
  align-items: center;
}

.subtitle-block {
  display: flex;
  gap: var(--spacing-025);
  align-items: center;
}

.steps-amount {
  margin-left: auto;
  font: var(--mobile-caption-1-regular);
  color: var(--text-primary);
}

.steps-amount-info {
  overflow: hidden;
  display: flex;
  gap: var(--spacing-025);
  align-items: center;

  text-overflow: ellipsis;
  white-space: nowrap;
}

.lock-icon {
  color: var(--icon-secondary);
}
</style>
